import { Pencil } from 'lucide-react';
import React, { useCallback, useId } from 'react';
import { usePostV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuImageMutation } from 'randmar-api-client';
import { useToast } from 'randmarcomps';
import { useManufacturer } from '../../contexts/WithManufacturer';
import InlineSpinner from "../util/InlineSpinner";

interface Props {
  randmarSKU?: string;
  children: React.ReactNode;
  disabled?: boolean;
}

const EditProductImageWrapper = ({ randmarSKU, children, disabled }: Props) => {
  const { applicationId } = useManufacturer();
  const fileInputId = useId();
  const { toast } = useToast();
  const [uploadImage, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuImageMutation();

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      toast({
        variant: 'destructive',
        title: 'No file selected',
        description: 'Please select an image file to upload.',
      });
      return;
    }

    if (!randmarSKU) {
      toast({
        variant: 'destructive',
        title: 'Missing SKU',
        description: 'Cannot upload image without a valid SKU.',
      });
      return;
    }
    if (!applicationId) {
      toast({
        variant: 'destructive',
        title: 'Missing manufacturer',
        description: 'Unable to determine the current manufacturer. Please reload and try again.',
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const wasSuccessful = await uploadImage({
        routeManufacturerId: applicationId,
        randmarSku: randmarSKU,
        body: formData as unknown as { file?: Blob },
      }).unwrap();

      if (wasSuccessful) {
        toast({
          variant: 'success',
          title: 'Success',
          description: 'Product image uploaded successfully.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Upload failed',
          description: 'The server was unable to process the image. Please try again.',
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: 'There was a problem uploading your image. Please try again.',
      });
      console.error('Failed to upload product image:', error);
    }
    event.target.value = '';
  }, [applicationId, randmarSKU, toast, uploadImage]);

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        id={fileInputId}
        disabled={isLoading}
      />
      <label className="contents" htmlFor={fileInputId}>
        <div className="relative cursor-pointer group">
          {children}
          {!isLoading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-md">
              <Pencil className="h-8 w-8 text-white" />
            </div>
          )}
          {isLoading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-md cursor-wait">
              <InlineSpinner size="lg" tone="accent" decorative />
            </div>
          )}
        </div>
      </label>
    </>
  );
};

export default EditProductImageWrapper;
